import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  get routeProtectionHandle() {
    return this.refs.cartBubble?.dataset?.routeProtectionHandle || 'routeins';
  }

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    const safeValue = Number.isFinite(value) ? value : 0;
    this.refs.cartBubbleCount.textContent = safeValue < 100 ? String(safeValue) : '99+';
  }

  connectedCallback() {
    super.connectedCallback();

    this.#syncWithServerCount();
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = await this.#resolveDisplayCount(event);
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    if (comingFromProductForm && event.target === document) {
      return;
    }

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Returns displayed cart count excluding route protection quantity.
   * @param {CartUpdateEvent} event
   * @returns {Promise<number>}
   */
  #resolveDisplayCount = async (event) => {
    const cartResource = event?.detail?.resource;
    if (cartResource?.items) {
      return this.#getDisplayCountFromItems(cartResource.items);
    }

    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      const cart = await response.json();
      return this.#getDisplayCountFromItems(cart?.items || []);
    } catch (_error) {
      return event?.detail?.data?.itemCount ?? 0;
    }
  };

  /**
   * Computes cart bubble count excluding route protection line quantity.
   * @param {Array<any>} items
   * @returns {number}
   */
  #getDisplayCountFromItems = (items) => {
    const handle = this.routeProtectionHandle.toLowerCase();
    let total = 0;
    for (const item of items || []) {
      const quantity = Number(item?.quantity) || 0;
      const itemHandle = (item?.handle || item?.product_handle || '').toString().toLowerCase();
      const itemUrl = (item?.url || '').toString().toLowerCase();
      const isRouteProtection = itemHandle === handle || itemUrl.includes(`/products/${handle}`);
      if (!isRouteProtection) {
        total += quantity;
      }
    }
    return Math.max(total, 0);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const serverCount = parseInt(this.refs.cartBubble?.dataset.cartCount ?? '', 10);
    if (!Number.isNaN(serverCount)) {
      this.renderCartBubble(serverCount, false, false);
      return;
    }

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };

  #syncWithServerCount() {
    if (!this.refs.cartBubble) return;
    const serverCount = parseInt(this.refs.cartBubble.dataset.cartCount ?? '', 10);
    if (Number.isNaN(serverCount)) return;
    this.renderCartBubble(serverCount, false, false);
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
